import { type ReactNode } from 'react';
import { computeTableGrid, isNumericDisplay } from './tableFormula';

/**
 * MarkdownView — a small, dependency-free markdown renderer for spec documents.
 *
 * Intentionally minimal: it supports the subset Spec Studio docs use — ATX
 * headings, fenced code blocks, unordered/ordered lists, blockquotes, and
 * inline `code` / **bold** / *italic*. It renders to React elements (never
 * `dangerouslySetInnerHTML`), so untrusted markdown cannot inject markup.
 *
 * GFM tables additionally support spreadsheet-style FORMULAS: a cell whose
 * text starts with '=' (e.g. `=E2-F2`, `=ROUND(J2,1)`) is COMPUTED like a
 * spreadsheet via {@link computeTableGrid}. Tables with no formula cells render
 * exactly as before.
 */

interface MarkdownViewProps {
  markdown: string;
  className?: string;
}

/** Returns a safe href for a markdown link, or null to render it as plain text. */
function safeHref(url: string): string | null {
  const candidate = url.trim();
  if (/^(https?:\/\/|mailto:)/i.test(candidate)) return candidate;
  if (candidate.startsWith('/') || candidate.startsWith('#')) return candidate;
  return null;
}

/** Renders inline spans: [text](url), **bold**, *italic*, and `code`. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Tokenize on the inline markers; links first, then the three emphasis markers.
  const regex = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-i${i++}`;
    if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      const href = linkMatch ? safeHref(linkMatch[2]) : null;
      if (linkMatch && href) {
        nodes.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-fg underline underline-offset-2 hover:no-underline"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={key} className="font-semibold text-zinc-100">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-surface-3 px-1.5 py-0.5 font-mono text-[0.85em] text-accent-fg"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(
        <em key={key} className="italic text-zinc-300">
          {token.slice(1, -1)}
        </em>,
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function MarkdownView({ markdown, className }: MarkdownViewProps) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  let i = 0;
  let key = 0;
  let listBuffer: { ordered: boolean; items: string[] } | null = null;

  const flushList = () => {
    if (!listBuffer) return;
    const { ordered, items } = listBuffer;
    const cls = 'my-2 space-y-1 pl-5 text-sm text-zinc-300';
    blocks.push(
      ordered ? (
        <ol key={`b${key++}`} className={`${cls} list-decimal`}>
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it, `l${key}-${idx}`)}</li>
          ))}
        </ol>
      ) : (
        <ul key={`b${key++}`} className={`${cls} list-disc`}>
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it, `l${key}-${idx}`)}</li>
          ))}
        </ul>
      ),
    );
    listBuffer = null;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block.
    if (line.trimStart().startsWith('```')) {
      flushList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        code.push(lines[i]);
        i++;
      }
      i++; // closing fence
      blocks.push(
        <pre
          key={`b${key++}`}
          className="my-3 overflow-x-auto rounded-xl border border-surface-border bg-surface-0 p-3 font-mono text-xs leading-relaxed text-zinc-300"
        >
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // GFM table: a header row followed by a |---|---| separator line.
    if (
      line.includes('|') &&
      i + 1 < lines.length &&
      /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(lines[i + 1])
    ) {
      flushList();
      const splitRow = (row: string): string[] => {
        let trimmed = row.trim();
        if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
        if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
        return trimmed.split('|').map((cell) => cell.trim());
      };
      const headerCells = splitRow(line);
      i += 2;
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        bodyRows.push(splitRow(lines[i]));
        i++;
      }
      // Evaluate spreadsheet-style formulas (=E2-F2, =ROUND(...), ...). When
      // the table has no formula cells the grid is identical to the raw rows
      // and `hasFormula` is false, so rendering is unchanged from before.
      const grid = computeTableGrid(headerCells, bodyRows);
      const rightAlign = (value: string): boolean =>
        grid.hasFormula && isNumericDisplay(value);
      blocks.push(
        <div key={`b${key++}`} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm text-zinc-300">
            <thead>
              <tr>
                {grid.header.map((cell, cellIndex) => (
                  <th
                    key={cellIndex}
                    className={`border border-surface-border bg-surface-2 px-3 py-1.5 font-semibold text-zinc-100 ${rightAlign(cell) ? 'text-right' : 'text-left'}`}
                  >
                    {renderInline(cell, `th${key}-${cellIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.body.map((cells, rowIndex) => (
                <tr key={rowIndex}>
                  {cells.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={`border border-surface-border px-3 py-1.5 align-top${rightAlign(cell) ? ' text-right' : ''}`}
                    >
                      {renderInline(cell, `td${key}-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Headings.
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      flushList();
      const level = heading[1].length;
      const content = renderInline(heading[2], `h${key}`);
      const sizes = ['text-lg', 'text-base', 'text-sm', 'text-sm'];
      const HeadingTag = (['h1', 'h2', 'h3', 'h4'][level - 1] ?? 'h4') as
        | 'h1'
        | 'h2'
        | 'h3'
        | 'h4';
      blocks.push(
        <HeadingTag
          key={`b${key++}`}
          className={`mt-4 mb-1 font-semibold text-zinc-100 ${sizes[level - 1]}`}
        >
          {content}
        </HeadingTag>,
      );
      i++;
      continue;
    }

    // Blockquote.
    if (line.startsWith('>')) {
      flushList();
      blocks.push(
        <blockquote
          key={`b${key++}`}
          className="my-2 border-l-2 border-accent/50 pl-3 text-sm italic text-zinc-400"
        >
          {renderInline(line.replace(/^>\s?/, ''), `q${key}`)}
        </blockquote>,
      );
      i++;
      continue;
    }

    // Ordered list item.
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (ordered) {
      if (!listBuffer || !listBuffer.ordered) {
        flushList();
        listBuffer = { ordered: true, items: [] };
      }
      listBuffer.items.push(ordered[1]);
      i++;
      continue;
    }

    // Unordered list item.
    const unordered = /^\s*[-*]\s+(.*)$/.exec(line);
    if (unordered) {
      if (!listBuffer || listBuffer.ordered) {
        flushList();
        listBuffer = { ordered: false, items: [] };
      }
      listBuffer.items.push(unordered[1]);
      i++;
      continue;
    }

    // Blank line ends a list/paragraph.
    if (line.trim() === '') {
      flushList();
      i++;
      continue;
    }

    // Paragraph.
    flushList();
    blocks.push(
      <p key={`b${key++}`} className="my-2 text-sm leading-relaxed text-zinc-300">
        {renderInline(line, `p${key}`)}
      </p>,
    );
    i++;
  }
  flushList();

  return <div className={className}>{blocks}</div>;
}

export default MarkdownView;
